import React from "react";
import moment from "moment";
import IconComponent from "../../../components/common/IconComponent";
// import { formatIndianNumber } from "../../../_helper/helper";
import {
  FaCalendarAlt, FaUsers, FaEnvelope, FaPhoneAlt,
  FaMapMarkerAlt, FaCheck, FaTimes,
  FaClock
} from "react-icons/fa";

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────
const getOrdinal = (n: number) => {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

const fmt = (n: any) => {
  const val = parseFloat(n) || 0;
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(val);
};

/**
 * Formats minutes into "X Day Y Hr Z Min"
 */
const formatDuration = (totalMinutes: number) => {
  if (!totalMinutes) return "";
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;

  const parts = [];
  if (days > 0) parts.push(`${days} Day${days > 1 ? 's' : ''}`);
  if (hours > 0) parts.push(`${hours} Hr${hours > 1 ? 's' : ''}`);
  if (minutes > 0) parts.push(`${minutes} Min${minutes > 1 ? 's' : ''}`);

  return parts.join(" ");
};

interface B2BQuotationDocumentProps {
  data: any;
}


const B2BQuotationDocument: React.FC<B2BQuotationDocumentProps> = ({ data }) => {
  const company = data.CompanyDetails || {
    CompanyName: "AICRM",
    CompanyLogo: "",
    Address: "Ahmedabad, Gujarat, India",
    EmailID: "support@aicrm.com",
    MobileNo: "+91 94270 05604"
  };

  // ── Calculate Transport & Activities Totals ───────────────────────────────
  const transportTotals = (data.TransportsAndActivities?.ServiceGroups || []).reduce((acc: number, group: any) => {
    return acc + (group.Services || []).reduce((sAcc: number, svc: any) => {
      return sAcc + (svc.Subservices || []).reduce((subAcc: number, sub: any) => {
        return subAcc + (sub.DailyPrices || []).reduce((dpAcc: number, dp: any) => {
          return dpAcc + ((dp.GivenPrice || 0) * (sub.Qty || 1));
        }, 0);
      }, 0);
    }, 0);
  }, 0);

  const extraTransTotal = (data.TransportsAndActivities?.ExtraTransportationServices || []).reduce((acc: number, e: any) => acc + (e.TotalPrice || 0), 0);
  const otherSpecialTotal = (data.OtherSpecialServices || []).reduce((acc: number, os: any) => acc + (os.TotalPrice || 0), 0);

  const totalTransAndActivities = transportTotals + extraTransTotal + otherSpecialTotal;

  // ── Prepare Sorted Transport & Activities Rows ───────────────────────────
  const sortedTransRows: any[] = [];

  // 1. Flatten Main Services
  (data.TransportsAndActivities?.ServiceGroups || []).forEach((group: any) => {
    (group.Services || []).forEach((svc: any) => {
      (svc.Subservices || []).forEach((sub: any) => {
        (sub.DailyPrices || []).forEach((dp: any, dpIdx: number) => {
          sortedTransRows.push({
            type: 'MAIN',
            dayNo: dp.Day || dp.DayNo || (dpIdx + 1),
            date: dp.Date,
            svc: svc,
            sub: sub,
            price: (dp.GivenPrice || 0) * (sub.Qty || 1),
            originalDp: dp
          });
        });
      });
    });
  });

  // 2. Flatten Extra Transport
  (data.TransportsAndActivities?.ExtraTransportationServices || []).forEach((e: any) => {
    sortedTransRows.push({
      type: 'EXTRA',
      dayNo: e.DayNo || e.Day,
      date: e.Date,
      name: e.ServiceName,
      price: e.TotalPrice,
      comment: e.Comment
    });
  });

  // 3. Flatten Special Services
  (data.OtherSpecialServices || []).forEach((os: any) => {
    sortedTransRows.push({
      type: 'SPECIAL',
      dayNo: os.DayNo || os.Day,
      date: os.Date,
      name: os.ServiceName,
      price: os.TotalPrice,
      comment: os.Comment
    });
  });

  // 4. Final Sort by Day Number
  sortedTransRows.sort((a, b) => Number(a.dayNo || 0) - Number(b.dayNo || 0));

  return (
    <div className="b2b-quotation-document">

      {/* ── Official Document Header ────────────────────────────────────────── */}
      <header className="quote-official-header">
        <div className="brand-section">
          {company.CompanyLogo ? (
            <img src={company.CompanyLogo} alt={company.CompanyName} style={{ maxHeight: "80px", marginBottom: "15px" }} />
          ) : (
            <div className="logo-placeholder">{company.CompanyName.split(' ')[0]}<span>{company.CompanyName.split(' ')[1] || ''}</span></div>
          )}
          <div className="company-info">
            <div className="fw-bold fs-5 text-dark mb-1">{company.CompanyName}</div>
            {company.Address && <div className="mb-1">{company.Address}</div>}
            <div className="mb-1">
              {company.CityName}{company.StateName && `, ${company.StateName}`}{company.Pincode && ` - ${company.Pincode}`}
            </div>
            <div className="small">
              {company.EmailID} | {company.MobileNo}
            </div>
            {(company.GSTNo || company.PANNo) && (
              <div className="mt-2 small text-muted">
                {company.GSTNo && <span className="me-3">GST: <b>{company.GSTNo}</b></span>}
                {company.PANNo && <span>PAN: <b>{company.PANNo}</b></span>}
              </div>
            )}
          </div>
        </div>
        <div className="quote-meta">
          <div className="label">Quotation</div>
          <div className="details">Ref No: <span>{data.QuotationReference || `#${data.ID}`}</span></div>
          <div className="details">Date: <span>{moment().format("DD MMM, YYYY")}</span></div>
        </div>
      </header>

      {/* ── Bank Details ────────────────────────── */}
      {company.BankName && (
        <div className="company-bank-bar no-print-bg mb-4">
          <div className="small text-uppercase fw-bold text-muted mb-1" style={{ fontSize: '10px', letterSpacing: '1px' }}>Banking Details</div>
          <div className="d-flex flex-wrap gap-x-4 small">
            <span>Bank: <b>{company.BankName}</b></span>
            <span>A/C: <b>{company.BankAccountNo}</b></span>
            {company.BankIFSCCode && <span>IFSC: <b>{company.BankIFSCCode}</b></span>}
            <span>Branch: {company.BankBranch}</span>
          </div>
        </div>
      )}

      {/* ── Client & Trip Summary ───────────────────────────────────────────── */}
      {(data.FirstName || data.LastName || data.EmailID || data.MobileNo || data.Mobile || data.TripDestinationName || data.Nights || data.TripStartDate) && (
        <section className="brief-summary-section">
          {(data.FirstName || data.LastName || data.EmailID || data.MobileNo || data.Mobile) && (
            <div className="summary-block">
              <div className="title">Prepared For</div>
              {(data.FirstName || data.LastName) && (
                <div className="info-row">
                  <IconComponent icon={FaUsers} className="icon" />
                  <div className="value">{data.Initial} {data.FirstName} {data.LastName}</div>
                </div>
              )}
              {data.EmailID && (
                <div className="info-row">
                  <IconComponent icon={FaEnvelope} className="icon" />
                  <div className="value">{data.EmailID}</div>
                </div>
              )}
              {(data.MobileNo || data.Mobile) && (
                <div className="info-row">
                  <IconComponent icon={FaPhoneAlt} className="icon" />
                  <div className="value">{data.MobileNo || data.Mobile}</div>
                </div>
              )}
            </div>
          )}

          {(data.TripDestinationName || data.Nights || data.TripStartDate) && (
            <div className="summary-block">
              <div className="title">Trip Overview</div>
              {data.TripDestinationName && (
                <div className="info-row">
                  <IconComponent icon={FaMapMarkerAlt} className="icon" />
                  <span className="label">Destination:</span>
                  <div className="value">{data.TripDestinationName}</div>
                </div>
              )}
              {data.Nights > 0 && (
                <div className="info-row">
                  <IconComponent icon={FaCalendarAlt} className="icon" />
                  <span className="label">Duration:</span>
                  <div className="value">{data.Nights} Nights / {data.Nights + 1} Days</div>
                </div>
              )}
              {data.TripStartDate && (
                <div className="info-row">
                  <IconComponent icon={FaClock} className="icon" />
                  <span className="label">Travel Date:</span>
                  <div className="value">{moment(data.TripStartDate).format("DD MMM, YYYY")}</div>
                </div>
              )}
              <div className="info-row">
                <IconComponent icon={FaUsers} className="icon" />
                <span className="label">Guests:</span>
                <div className="value">
                  {data.Adults} Adults{data.Children > 0 ? `, ${data.Children} Child` : ''}{data.Infants > 0 ? `, ${data.Infants} Infant` : ''}
                </div>
              </div>
            </div>
          )}
        </section>
      )}

      {/* ── Package Options Comparison Grid ─────────────────────────────────── */}
      {(data.PackageOptions || []).length > 0 && (
        <section className="package-comparison-section">
          <div className="comparison-grid">
            {(data.PackageOptions || []).map((opt: any, idx: number) => (
              <div className="comparison-box" key={idx}>
                <div className="opt-label">Option {idx + 1}</div>
                <div className="opt-name">{opt.OptionName}</div>
                <div className="opt-total">{fmt(opt.GrandTotal)}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ── Accommodation Proposal ─────────────────────────────────────────── */}
      {(data.PackageOptions || []).length > 0 && (
        <h4 className="view-section-title">Accommodation Proposal</h4>
      )}
      {(data.PackageOptions || []).map((opt: any, optIdx: number) => {
        const accommodationTotal = (opt.Hotels || []).reduce((acc: number, h: any) =>
          acc + (h.NightPrices || []).reduce((nightAcc: number, np: any) =>
            nightAcc + ((parseFloat(np.GivenPrice) || 0) * (parseFloat(h.NoOfRooms) || 1))
            , 0)
          , 0);

        const inclusionsTotal = (opt.HotelSpecialInclusions || []).reduce((acc: number, incl: any) =>
          acc + (parseFloat(incl.TotalPrice) || 0)
          , 0);

        const totalHotelAccommodation = accommodationTotal + inclusionsTotal;

        return (
          <div className="proposal-option" key={optIdx}>
            <div className="option-header">
              <span className="name">Option {optIdx + 1}: {opt.OptionName}</span>
              <span className="p-badge">Recommended</span>
            </div>
            <table className="hotel-table">
              <thead>
                <tr>
                  <th style={{ width: '130px' }}>Nights</th>
                  <th>Hotel & City</th>
                  <th>Room & Meal Plan</th>
                  <th className="text-end">Package Cost</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const hotelNights = (opt.Hotels || []).flatMap((h: any) =>
                    (h.NightPrices || []).map((np: any) => ({ ...h, ...np, detailType: 'HOTEL' }))
                  );
                  const inclusions = (opt.HotelSpecialInclusions || []).map((incl: any) => ({
                    ...incl,
                    Night: incl.Night,
                    Date: incl.Date,
                    detailType: 'INCLUSION'
                  }));

                  const accommodationRows = [...hotelNights, ...inclusions].sort((a, b) => {
                    const diff = Number(a.Night || 0) - Number(b.Night || 0);
                    if (diff !== 0) return diff;
                    if (a.detailType === 'HOTEL' && b.detailType === 'INCLUSION') return -1;
                    if (a.detailType === 'INCLUSION' && b.detailType === 'HOTEL') return 1;
                    return 0;
                  });

                  return accommodationRows.map((row: any, rIdx: number) => {
                    const isSameNight = rIdx > 0 && Number(accommodationRows[rIdx - 1].Night) === Number(row.Night);

                    return (
                      <tr key={rIdx} className={row.detailType === 'INCLUSION' ? 'inclusion-row-document' : ''}>
                        <td className="text-center night-col-sembark">
                          {!isSameNight && (
                            <div className="night-badge">
                              {getOrdinal(row.Night)}
                              <small>{moment(row.Date).format("DD MMM")}</small>
                            </div>
                          )}
                        </td>

                        {row.detailType === 'HOTEL' ? (
                          <>
                            <td>
                              <div>
                                <div className="h-name">{row.HotelName}</div>
                                <div className="meal-plan d-flex align-items-center gap-2">
                                  {data.TripDestinationName}
                                  {row.HotelCategory && (
                                    <span className="cat-star">★ {row.HotelCategory} Star</span>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td>
                              <div className="room-type">{row.NoOfRooms} x {row.RoomTypeName}</div>
                              <div className="meal-plan">
                                {row.MealPlanName} | {row.Adults} Adults{row.CWEB > 0 ? `, ${row.CWEB} Child` : ''}{row.CNB > 0 ? `, ${row.CNB} CNB` : ''}
                              </div>
                            </td>
                            <td className="text-end fw-bold">
                              {fmt(row.GivenPrice * (row.NoOfRooms || 1))}
                            </td>
                          </>
                        ) : (
                          <>
                            <td colSpan={2}>
                              <span className="inclusion-label-badge" style={{ background: '#0d9488', color: '#fff', border: 'none' }}>Inclusion</span>
                              <div className="h-name">{row.ServiceName || row.Service}</div>
                              {row.Comment && <div className="small text-muted italic">Note: {row.Comment}</div>}
                            </td>
                            <td className="text-end fw-bold">
                              <span style={{ color: '#0d9488' }}>{fmt(row.TotalPrice)}</span>
                            </td>
                          </>
                        )}
                      </tr>
                    );
                  });
                })()}
              </tbody>
            </table>
            <div className="option-pricing-footer">
              <div className="price-item">
                <span className="label">Accommodation Subtotal</span>
                <span className="value">{fmt(totalHotelAccommodation)}</span>
              </div>
            </div>
          </div>
        );
      })}

      {/* ── Transports & Activities ─────────────────────────────────────────── */}
      {sortedTransRows.length > 0 && (
        <>
          <h4 className="view-section-title">Transport & Activities</h4>
          <div className="proposal-option mb-5">
            <div className="option-header">
              <span className="name">Services & Itinerary Breakdown</span>
              <span className="p-badge" style={{ background: '#64748b' }}>Complete Details</span>
            </div>
            <table className="service-table hotel-table">
              <thead>
                <tr>
                  <th style={{ width: '130px' }}>Day</th>
                  <th>Service</th>
                  <th>Service Detail</th>
                  <th className="text-end">Cost</th>
                </tr>
              </thead>
              <tbody>
                {sortedTransRows.map((row: any, rIdx: number) => {
                  const isSameDay = rIdx > 0 && Number(sortedTransRows[rIdx - 1].dayNo) === Number(row.dayNo);
                  return (
                    <tr key={rIdx} className={row.type === 'EXTRA' ? 'extra-service-row' : row.type === 'SPECIAL' ? 'special-service-row' : ''}>
                      {/* Column 1: Day */}
                      <td>
                        {!isSameDay && (
                          <div className="night-info">
                            Day {row.dayNo}
                            {row.date && <small>{moment(row.date).format("DD MMM")}</small>}
                          </div>
                        )}
                      </td>

                      {/* Column 2 & 3: Details */}
                      {row.type === 'MAIN' ? (
                        <>
                          <td>
                            <div className="d-flex align-items-center gap-2 mb-1">
                              <span className={`badge-svc-type ${(row.svc.Type || "").toLowerCase()}`}>{row.svc.Type}</span>
                              <div className="h-name">{row.svc.ServiceName}</div>
                            </div>
                            {row.svc.ServiceTypeName && <div className="meal-plan italic">({row.svc.ServiceTypeName})</div>}
                          </td>
                          <td>
                            <div className="room-type fw-bold">{row.sub.Qty}x {row.sub.TypeName}</div>
                            {row.svc.Type === 'ACTIVITY' && (row.svc.Slot || row.svc.DurationMinutes > 0) && (
                              <div className="small text-info mt-1 fw-semibold">
                                {row.svc.Slot && <span>Slot: {row.svc.Slot}</span>}
                                {row.svc.DurationMinutes > 0 && <span className="ms-2">Duration: {formatDuration(row.svc.DurationMinutes)}</span>}
                              </div>
                            )}
                            {row.svc.Comment && <div className="mt-1 small text-muted italic">Note: {row.svc.Comment}</div>}
                          </td>
                        </>
                      ) : (
                        <td colSpan={2}>
                          <span
                            className="inclusion-label-badge"
                            style={{
                              background: row.type === 'EXTRA' ? '#64748b' : '#7c3aed',
                              color: '#fff', border: 'none'
                            }}
                          >
                            {row.type === 'EXTRA' ? 'EXTRA' : 'SPECIAL'}
                          </span>
                          <div className="h-name">{row.name}</div>
                          {row.comment && <div className="small text-muted italic mt-1">Note: {row.comment}</div>}
                        </td>
                      )}

                      {/* Column 4: Price */}
                      <td className="text-end fw-bold">
                        <span style={{ color: row.type === 'EXTRA' ? '#64748b' : row.type === 'SPECIAL' ? '#7c3aed' : 'inherit' }}>
                          {fmt(row.price)}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="option-pricing-footer">
              <div className="price-item">
                <span className="label">Transport & Activities Subtotal</span>
                <span className="value">{fmt(totalTransAndActivities)}</span>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ── Tour Itinerary ──────────────────────────────────────────────────── */}
      {data.Itinary?.length > 0 && (
        <>
          <h4 className="view-section-title">Daywise Itinerary</h4>
          <div className="professional-timeline">
            {data.Itinary.map((it: any, itIdx: number) => (
              <div className="timeline-day" key={itIdx}>
                <span className="day-marker">Day {it.DayNo}</span>
                <div className="day-title">
                  {it.ItineraryTitle}
                  {it.Place && <span>@{it.Place}</span>}
                </div>
                <div className="day-content">
                  <div className="description" dangerouslySetInnerHTML={{ __html: it.Description }} />
                  {it.ItineraryImage && (
                    <img src={it.ItineraryImage} alt={`Day ${it.DayNo}`} className="day-image" />
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* ── Inclusions & Exclusions ─────────────────────────────────────────── */}
      {(data.Inclusion || data.Exclusion) && (
        <>
          <h4 className="view-section-title">Inclusions & Exclusions</h4>
          <div className="policy-grid mb-5">
            {data.Inclusion && (
              <div className="policy-box">
                <div className="p-title incl">
                  <IconComponent icon={FaCheck} /> Inclusions
                </div>
                <div className="p-content" dangerouslySetInnerHTML={{ __html: data.Inclusion }} />
              </div>
            )}
            {data.Exclusion && (
              <div className="policy-box">
                <div className="p-title excl">
                  <IconComponent icon={FaTimes} /> Exclusions
                </div>
                <div className="p-content" dangerouslySetInnerHTML={{ __html: data.Exclusion }} />
              </div>
            )}
          </div>
        </>
      )}

      {/* ── Final Remarks ───────────────────────────────────────────────────── */}
      {data.ImportantNotes && (
        <>
          <h4 className="view-section-title">Important Notes</h4>
          <div className="policy-box mb-5">
            <div className="p-content" dangerouslySetInnerHTML={{ __html: data.ImportantNotes }} />
          </div>
        </>
      )}

      {data.TermsConditions && (
        <>
          <h4 className="view-section-title">Terms & Conditions</h4>
          <div className="policy-box mb-5">
            <div className="p-content" dangerouslySetInnerHTML={{ __html: data.TermsConditions }} />
          </div>
        </>
      )}

      <div className="text-end mt-5 pt-5 opacity-75">
        {data.CreatedByName && <div className="fw-bold">{data.CreatedByName}</div>}
        <div>{company.CompanyName}</div>
        <div className="small">{moment().format("DD/MM/YYYY HH:mm")}</div>
      </div>
    </div>
  );
};

export default B2BQuotationDocument;
